/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {extendTheme} from '@salesforce/retail-react-app/app/components/shared/ui'

// Foundational style overrides
import styles from './foundations/styles'
import colors from './foundations/colors'
import gradients from '@salesforce/retail-react-app/app/theme/foundations/gradients'
import sizes from './foundations/sizes'
import space from '@salesforce/retail-react-app/app/theme/foundations/space'
import layerStyles from './foundations/layerStyles'
import textStyles from './foundations/textStyles'
import shadows from './foundations/shadows'
import typography from './foundations/typography'

// Base component style overrides
import Alert from '@salesforce/retail-react-app/app/theme/components/base/alert'
import Accordion from '@salesforce/retail-react-app/app/theme/components/base/accordion'
import Badge from './components/base/badge'
import Button from './components/base/button'
import Checkbox from './components/base/checkbox'
import Container from '@salesforce/retail-react-app/app/theme/components/base/container'
import Drawer from '@salesforce/retail-react-app/app/theme/components/base/drawer'
import FormLabel from './components/base/formLabel'
import Heading from './components/base/heading'
import Icon from '@salesforce/retail-react-app/app/theme/components/base/icon'
import Input from './components/base/input'
import Modal from '@salesforce/retail-react-app/app/theme/components/base/modal'
import Radio from './components/base/radio'
import Select from './components/base/select'
import Skeleton from '@salesforce/retail-react-app/app/theme/components/base/skeleton'
import Tooltip from './components/base/tooltip'
import Popover from '@salesforce/retail-react-app/app/theme/components/base/popover'

// Project Component style overrides
import App from '@salesforce/retail-react-app/app/theme/components/project/_app'
import Benefits from './components/project/benefits'
import Breadcrumb from '@salesforce/retail-react-app/app/theme/components/project/breadcrumb'
import ContactUs from './components/project/contactUs'
import Header from './components/project/header'
import ListMenu from './components/project/list-menu'
import Footer from './components/project/footer'
import CheckoutFooter from './components/project/checkout-footer'
import GeneralVideo from './components/project/general-video'
import LandingPageHero from './components/project/landing-page-hero'
import LandingPageDataSlider from './components/project/landing-page-data-slider'
import LandingPageDiscover from './components/project/landing-page-discover'
import Login from './components/project/login'
import LinksList from './components/project/links-list'
import DrawerMenu from './components/project/drawer-menu'
import NestedAccordion from './components/project/nested-accordion'
import LocaleSelector from './components/project/locale-selector'
import OfflineBanner from '@salesforce/retail-react-app/app/theme/components/project/offline-banner'
import Pagination from '@salesforce/retail-react-app/app/theme/components/project/pagination'
import ProductFeatures from './components/project/product-features'
import ProductTile from '@salesforce/retail-react-app/app/theme/components/project/product-tile'
import SocialIcons from './components/project/social-icons'
import SwatchGroup from './components/project/swatch-group'
import ImageGallery from '@salesforce/retail-react-app/app/theme/components/project/image-gallery'
import ImageTextTile from './components/project/image-text-tile'
import SelectRoleModal from './components/project/select-role-modal'

// Please refer to the Chakra-Ui theme customization docs found
// here https://chakra-ui.com/docs/theming/customize-theme to learn
// more about extending and overriding themes for your project.
export const overrides = {
    styles,
    ...typography,
    layerStyles,
    textStyles,
    colors,
    sizes,
    space,
    gradients,
    shadows,
    components: {
        // base components
        Accordion,
        Alert,
        Badge,
        Button,
        Checkbox,
        Container,
        Drawer,
        FormLabel,
        Heading,
        Icon,
        Input,
        Modal,
        Popover,
        Radio,
        Select,
        Skeleton,
        Tooltip,

        // project components
        App,
        Benefits,
        Breadcrumb,
        ContactUs,
        Header,
        Footer,
        CheckoutFooter,
        GeneralVideo,
        LandingPageHero,
        LandingPageDataSlider,
        LandingPageDiscover,
        Login,
        LinksList,
        ListMenu,
        DrawerMenu,
        NestedAccordion,
        LocaleSelector,
        OfflineBanner,
        SocialIcons,
        Pagination,
        ProductFeatures,
        ProductTile,
        SwatchGroup,
        ImageGallery,
        ImageTextTile,
        SelectRoleModal
    }
}

export default extendTheme(overrides)
